import { upperFirst } from 'lodash-es';
import type { CSSProperties, ReactNode } from 'react';
import Tooltip from '../../../utils/tooltip';

interface TooltipContentProps {
    title: string;
    date: string;
    dateLabel?: string;
    additionalContent?: ReactNode;
}

function TooltipContent({
    title,
    date,
    dateLabel,
    additionalContent,
}: TooltipContentProps): JSX.Element {
    return (
        <>
            <p>
                {upperFirst(gettext('MEASURING_POINT'))}: {title}
            </p>
            <p>{`${dateLabel}: ${date}`}</p>
            {additionalContent}
        </>
    );
}

function getBackgroundStyle(color: string): CSSProperties {
    return {
        backgroundImage: `repeating-linear-gradient(
          135deg,
          transparent,
          transparent 4px,
          ${color} 4px,
          ${color} 8px
        )`,
    };
}

interface BaseContactIndicatorProps {
    left: number;
    width: number;
    title: string;
    date: string;
    dateLabel?: string;
    additionalContent?: ReactNode;
    id: string;
    color?: CSSProperties;
    bgClass?: string;
}

function BaseContactIndicator({
    left,
    width,
    title,
    date,
    dateLabel,
    additionalContent,
    id,
    color,
    bgClass,
}: BaseContactIndicatorProps): JSX.Element {
    return (
        <Tooltip
            content={
                <TooltipContent
                    title={title}
                    date={date}
                    dateLabel={dateLabel}
                    additionalContent={additionalContent}
                />
            }
            followCursor={true}
            placement="bottom"
        >
            <div
                style={{
                    left,
                    width,
                    ...(color ?? {}),
                }}
                className={`absolute z-10 h-full ${bgClass}`}
                data-testid={id}
            ></div>
        </Tooltip>
    );
}

export function LastContactIndicator(props: BaseContactIndicatorProps): JSX.Element {
    const color = getBackgroundStyle('rgba(0,0,0,0.025)');
    return (
        <BaseContactIndicator
            {...props}
            dateLabel={gettext('LAST_CONTACT')}
            bgClass="bg-grey-graph-overlay"
            color={color}
        />
    );
}

interface LastRecordIndicatorProps extends BaseContactIndicatorProps {
    uploadData?: unknown;
}

export function LastRecordIndicator(props: LastRecordIndicatorProps): JSX.Element {
    const color = getBackgroundStyle('rgba(248,218,40)');
    return (
        <BaseContactIndicator
            {...props}
            additionalContent={
                <p>
                    <>
                        {gettext('UPLOADING_DATA')}: {props.uploadData || gettext('No data found')}
                    </>
                </p>
            }
            dateLabel={gettext('LAST_KNOWN_RECORD')}
            bgClass="bg-yellow-graph-overlay opacity-25"
            color={color}
        />
    );
}
